import { useState, useMemo, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  CheckCircle2, AlertTriangle, Briefcase, Users, Sparkles, X, Plus, Lightbulb,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Dialog di conferma post-OCR per il bulk import.
 *
 * Props:
 *  - open: bool
 *  - previewData: {summary, results, new_commesse, ambiguous_employees}
 *  - employees: lista attuale
 *  - commesse: lista attuale
 *  - onCancel(): annulla tutto
 *  - onCompleted(savedSummary): chiamato dopo il salvataggio
 *  - onAnagraficaChanged(): callback per ricaricare le anagrafiche
 */
export default function ImportConfirmDialog({
  open,
  previewData,
  employees: initialEmployees = [],
  commesse: initialCommesse = [],
  onCancel,
  onCompleted,
  onAnagraficaChanged,
}) {
  const [employees, setEmployees] = useState(initialEmployees);
  const [commesse, setCommesse] = useState(initialCommesse);
  // Per ogni commessa nuova: {selected:true|false, codice, descrizione}
  const [commesseChoices, setCommesseChoices] = useState({});
  // Per ogni nome ambiguo: employee_id scelto OR "__new__" OR ""
  const [empChoices, setEmpChoices] = useState({});
  // Mapping per "crea nuovo dipendente" raw -> {cognome, nome, ruolo}
  const [newEmpForms, setNewEmpForms] = useState({});
  // raw -> bool, default true: salva l'alias quando l'utente abbina a un dipendente esistente
  const [learnAliases, setLearnAliases] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEmployees(initialEmployees);
  }, [initialEmployees]);
  useEffect(() => {
    setCommesse(initialCommesse);
  }, [initialCommesse]);

  // Initialize choices when previewData changes
  useEffect(() => {
    if (!previewData) return;
    const c = {};
    for (const nc of previewData.new_commesse || []) {
      c[nc.codice] = { selected: true, codice: nc.codice, descrizione: "", cliente: "" };
    }
    setCommesseChoices(c);
    const e = {};
    const ef = {};
    for (const ae of previewData.ambiguous_employees || []) {
      // Default: first candidate if any, else "__skip__"
      e[ae.raw] = ae.candidates?.[0]?.id || "__skip__";
      // Pre-fill new employee form from raw name
      const parts = (ae.raw || "").trim().split(/\s+/);
      const cognome = parts[0] || "";
      const nome = parts.slice(1).join(" ") || "";
      ef[ae.raw] = { cognome, nome, ruolo: "Operaio" };
    }
    setEmpChoices(e);
    setNewEmpForms(ef);
  }, [previewData]);

  const parsedRapportini = useMemo(() => {
    if (!previewData?.results) return [];
    return previewData.results
      .filter((r) => r.status === "ok" && r.data)
      .map((r) => r.data);
  }, [previewData]);

  // Set degli indici inclusi (default: tutti). L'utente può togliere il check
  // di un singolo rapportino dall'anteprima.
  const [includedIdx, setIncludedIdx] = useState(new Set());
  useEffect(() => {
    if (!parsedRapportini.length) {
      setIncludedIdx(new Set());
      return;
    }
    setIncludedIdx(new Set(parsedRapportini.map((_, i) => i)));
  }, [parsedRapportini]);

  const toggleIncluded = (idx) => {
    setIncludedIdx((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };
  const toggleAllIncluded = () => {
    if (includedIdx.size === parsedRapportini.length) {
      setIncludedIdx(new Set());
    } else {
      setIncludedIdx(new Set(parsedRapportini.map((_, i) => i)));
    }
  };

  const skippedCount = useMemo(() => {
    if (!previewData?.results) return 0;
    return previewData.results.filter((r) => r.status === "skipped").length;
  }, [previewData]);

  // Calcola warning "> 13h per dipendente in un giorno" sui rapportini parsati
  const overtimeWarnings = useMemo(() => {
    const out = [];
    for (const p of parsedRapportini) {
      for (const g of p.giorni || []) {
        const byEmp = {};
        for (const r of g.righe || []) {
          const key = r.employee_id || (r.employee_nome || "").trim().toLowerCase();
          if (!key) continue;
          if (!byEmp[key]) byEmp[key] = { label: r.employee_nome || "—", ore: 0 };
          byEmp[key].ore += Number(r.ore_tot || 0);
        }
        for (const k of Object.keys(byEmp)) {
          if (byEmp[k].ore > 13) {
            out.push({
              data: g.data,
              employee: byEmp[k].label,
              ore: Math.round(byEmp[k].ore * 10) / 10,
              numero: p.numero,
            });
          }
        }
      }
    }
    return out;
  }, [parsedRapportini]);

  // Check duplicati: chiama backend con tutte le (employee, data, commessa) coppie e mostra match
  const [duplicates, setDuplicates] = useState([]);
  // Set di "rapp_idx|day_idx|riga_idx" da scartare dall'import (default: nessuno)
  const [discardedKeys, setDiscardedKeys] = useState(new Set());
  useEffect(() => {
    if (!open || parsedRapportini.length === 0) {
      setDuplicates([]);
      return;
    }
    const pairs = [];
    for (const rap of parsedRapportini) {
      for (const g of rap.giorni || []) {
        if (!g.data) continue;
        for (const r of g.righe || []) {
          if (r.employee_id || r.employee_nome) {
            pairs.push({
              employee_id: r.employee_id || null,
              employee_nome: r.employee_nome || "",
              data: g.data,
              commessa_codice: r.commessa_codice || "",
              _new_ore_tot: Number(r.ore_tot || 0),
              _rapp_idx: parsedRapportini.indexOf(rap),
              _day_idx: rap.giorni.indexOf(g),
              _riga_idx: g.righe.indexOf(r),
            });
          }
        }
      }
    }
    if (pairs.length === 0) {
      setDuplicates([]);
      return;
    }
    let cancel = false;
    api.post("/rapportini/duplicates-check", { pairs })
      .then(({ data }) => {
        if (cancel) return;
        // Decora ogni duplicato col rapp/day/riga idx originale per poter scartare la riga giusta
        const decorated = (data?.duplicates || []).map((d) => {
          const match = pairs.find(
            (p) => p.data === d.data
              && (
                (p.employee_id && p.employee_id === d.employee_id)
                || (p.employee_nome && d.employee_nome && p.employee_nome.toLowerCase() === d.employee_nome.toLowerCase())
              )
              && (!d.input_commessa_codice || (p.commessa_codice || "").toLowerCase() === (d.input_commessa_codice || "").toLowerCase())
          );
          return match ? {
            ...d,
            _new_ore_tot: match._new_ore_tot,
            _rapp_idx: match._rapp_idx,
            _day_idx: match._day_idx,
            _riga_idx: match._riga_idx,
          } : d;
        });
        setDuplicates(decorated);
        setDiscardedKeys(new Set());
      })
      .catch((e) => { console.warn("[import] duplicates check failed:", e?.message); });
    return () => { cancel = true; };
  }, [open, parsedRapportini]);

  const finalize = async () => {
    setSaving(true);
    try {
      // 1) Create selected new commesse
      const createdCommesse = {}; // codice -> id
      for (const code of Object.keys(commesseChoices)) {
        const choice = commesseChoices[code];
        if (!choice.selected) continue;
        try {
          const { data } = await api.post("/commesse", {
            codice: choice.codice,
            descrizione: choice.descrizione || "",
            cliente: choice.cliente || "",
            attiva: true,
          });
          createdCommesse[choice.codice.toLowerCase()] = data.id;
        } catch (err) {
          // If already exists (race), look it up
          const existing = commesse.find(
            (c) => c.codice.toLowerCase() === choice.codice.toLowerCase()
          );
          if (existing) createdCommesse[choice.codice.toLowerCase()] = existing.id;
          else
            console.warn("[import] commessa create failed:", choice.codice, err?.message);
        }
      }

      // 2) Resolve ambiguous employees: either use chosen ID or create new
      const empMap = {}; // raw_name -> employee_id (or null = skip)
      const aliasTasks = [];
      for (const raw of Object.keys(empChoices)) {
        const choice = empChoices[raw];
        if (choice === "__skip__") {
          empMap[raw] = null;
        } else if (choice === "__new__") {
          const form = newEmpForms[raw];
          if (!form?.cognome) {
            empMap[raw] = null;
            continue;
          }
          try {
            const { data } = await api.post("/employees", {
              cognome: form.cognome,
              nome: form.nome,
              ruolo: form.ruolo || "Operaio",
              attivo: true,
            });
            empMap[raw] = data.id;
          } catch (err) {
            console.warn("[import] employee create failed:", raw, err?.message);
            empMap[raw] = null;
          }
        } else {
          empMap[raw] = choice;
          // Salva alias se l'utente l'ha richiesto (default true)
          if ((learnAliases[raw] ?? true) && raw) {
            aliasTasks.push(
              api.post(`/employees/${choice}/aliases`, { alias: raw })
                .catch((e) => console.warn("[import] alias save failed:", raw, e?.message))
            );
          }
        }
      }
      if (aliasTasks.length) await Promise.all(aliasTasks);

      // 3) Patch parsed rapportini with resolved IDs (solo i rapportini "inclusi")
      //    + filtra le righe in conflitto scartate dall'utente (discardedKeys)
      const patched = parsedRapportini
        .map((rap, idx) => {
          if (!includedIdx.has(idx)) return null;
          const giorni = (rap.giorni || []).map((g, dIdx) => ({
          ...g,
          righe: (g.righe || [])
            .map((rg, rIdx) => {
              // Se la riga è stata scartata dall'utente nel banner duplicati, droppala
              if (discardedKeys.has(`${idx}|${dIdx}|${rIdx}`)) return null;
              const updated = { ...rg };
              // Commessa: if not matched and we created it, set commessa_id
              const code = (rg.commessa_codice || "").toLowerCase();
              if (!updated.commessa_id && createdCommesse[code]) {
                updated.commessa_id = createdCommesse[code];
              }
              // Employee: if raw_name is ambiguous, apply the mapping
              const raw = (rg.employee_nome || "").trim();
              if (Object.prototype.hasOwnProperty.call(empMap, raw)) {
                const newId = empMap[raw];
                if (newId) {
                  updated.employee_id = newId;
                  const emp = employees.find((e) => e.id === newId);
                  if (emp) updated.employee_nome = `${emp.cognome} ${emp.nome}`;
                }
                // If newId is null, leave row but without employee_id (user can fix later)
              }
              return updated;
            })
            .filter(Boolean),  // rimuove le righe scartate (null)
        }));
          // Se dopo il filtraggio righe il rapportino è vuoto, scartalo del tutto
          const totalRighe = giorni.reduce((s, g) => s + (g.righe || []).length, 0);
          if (totalRighe === 0) return null;
          return { ...rap, giorni };
        })
        .filter(Boolean);

      if (patched.length === 0) {
        toast.warning("Nessun rapportino selezionato per il salvataggio");
        setSaving(false);
        return;
      }

      // 4) Finalize: save all rapportini
      const { data } = await api.post("/rapportini/import-finalize", {
        rapportini: patched,
      });
      toast.success(
        `Importati ${data.summary.created} rapportini (${data.summary.errors} errori)`
      );
      onAnagraficaChanged?.();
      onCompleted?.(data.summary);
    } catch (e) {
      console.error("[import] finalize failed:", e);
      const detail = e?.response?.data?.detail || "Salvataggio non riuscito";
      toast.error(typeof detail === "string" ? detail : "Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  if (!previewData) return null;

  const totRapportini = parsedRapportini.length;
  const newCommesseCount = Object.values(commesseChoices).filter((c) => c.selected).length;
  const newEmpCount = Object.values(empChoices).filter((v) => v === "__new__").length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onCancel?.()}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] flex flex-col"
        data-testid="import-confirm-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-2xl">
            <Sparkles className="h-6 w-6 text-[#0044FF]" />
            Conferma import ({totRapportini} rapportini)
          </DialogTitle>
          <DialogDescription>
            L'AI ha letto i file. Prima di salvare, conferma quali nuove
            <b> commesse</b> creare e abbina i <b>nomi ambigui</b> a dipendenti
            esistenti o nuovi.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {/* ANTEPRIMA RAPPORTINI DA SALVARE */}
          {parsedRapportini.length > 0 && (
            <Card className="p-0 border-blue-200 overflow-hidden">
              <div className="bg-blue-600 text-white px-4 py-2.5 flex items-center justify-between">
                <div className="font-heading font-bold">
                  Anteprima — {includedIdx.size} di {parsedRapportini.length} rapportini
                </div>
                <button
                  type="button"
                  onClick={toggleAllIncluded}
                  className="text-xs underline opacity-90 hover:opacity-100"
                  data-testid="import-toggle-all-button"
                >
                  {includedIdx.size === parsedRapportini.length ? "Deseleziona tutto" : "Seleziona tutto"}
                </button>
              </div>
              <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                {parsedRapportini.map((p, idx) => {
                  const isIncluded = includedIdx.has(idx);
                  const firstGiorno = (p.giorni && p.giorni[0]) || {};
                  const totOre = (p.giorni || []).reduce(
                    (s, g) => s + (g.righe || []).reduce((s2, r) => s2 + Number(r.ore_tot || 0), 0), 0
                  );
                  const emps = new Set();
                  const comms = new Set();
                  for (const g of (p.giorni || [])) {
                    for (const r of (g.righe || [])) {
                      if (r.employee_nome) emps.add(r.employee_nome);
                      if (r.commessa_codice) comms.add(r.commessa_codice);
                    }
                  }
                  return (
                    <label
                      key={`${p.numero ?? "n"}-${p.giorni?.[0]?.data ?? "d"}-${idx}`}
                      className={`flex items-start gap-3 px-4 py-2.5 text-sm cursor-pointer hover:bg-slate-50 ${
                        !isIncluded ? "opacity-50" : ""
                      }`}
                      data-testid={`import-rapp-row-${idx}`}
                    >
                      <input
                        type="checkbox"
                        checked={isIncluded}
                        onChange={() => toggleIncluded(idx)}
                        className="mt-1 h-4 w-4 accent-blue-600"
                        data-testid={`import-rapp-check-${idx}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="font-bold">
                            {p.data_inizio || firstGiorno.data || "?"}
                            {p.data_fine && p.data_fine !== (p.data_inizio || firstGiorno.data) && (
                              <> → {p.data_fine}</>
                            )}
                          </span>
                          <span className="text-xs text-slate-500">
                            {(p.giorni || []).length} gg · {totOre.toFixed(1)}h totali
                          </span>
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5 truncate">
                          <b>Dipendenti:</b> {[...emps].slice(0, 4).join(", ") || "—"}
                          {emps.size > 4 && ` · +${emps.size - 4}`}
                        </div>
                        <div className="text-xs text-slate-600 truncate">
                          <b>Commesse:</b> {[...comms].join(", ") || "—"}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </Card>
          )}

          {skippedCount > 0 && (
            <Card className="p-4 bg-slate-50 border-slate-200">
              <div className="text-sm text-slate-700">
                <b>{skippedCount}</b> pagin{skippedCount === 1 ? "a" : "e"} senza
                ore o senza dati leggibili {skippedCount === 1 ? "è stata scartata" : "sono state scartate"} (evita
                duplicati fantasma).
              </div>
            </Card>
          )}

          {/* Warning: ore > 13 per dipendente/giorno */}
          {overtimeWarnings.length > 0 && (
            <Card
              className="p-4 border-amber-300 bg-amber-50"
              data-testid="import-overtime-banner"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
                <div className="flex-1 text-sm">
                  <div className="font-bold text-amber-900 mb-1">
                    Oltre 13 ore in un giorno
                  </div>
                  <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                    {overtimeWarnings.slice(0, 15).map((w, i) => (
                      <li key={`${w.numero}-${w.employee}-${w.data}-${i}`} className="text-amber-800">
                        Rap. #{w.numero} · <b>{w.employee}</b> · {w.data} → <b>{w.ore}h</b>
                      </li>
                    ))}
                    {overtimeWarnings.length > 15 && (
                      <li className="italic text-amber-700">
                        ... e altri {overtimeWarnings.length - 15}
                      </li>
                    )}
                  </ul>
                  <div className="text-xs text-amber-700 mt-1.5">
                    Verifica nei dati estratti prima di confermare.
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Possibili duplicati: scegli per ogni riga */}
          {duplicates.length > 0 && (
            <Card
              className="p-0 overflow-hidden border-amber-300"
              data-testid="import-duplicates-banner"
            >
              <div className="bg-amber-500 text-white px-4 py-2.5 flex items-baseline justify-between">
                <div className="font-heading font-bold flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Possibili duplicati ({duplicates.length})
                </div>
                <div className="text-xs">
                  <b>{duplicates.length - discardedKeys.size}</b> da importare ·{" "}
                  <b>{discardedKeys.size}</b> scartate
                </div>
              </div>
              <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
                {duplicates.map((d, i) => {
                  const key = `${d._rapp_idx}|${d._day_idx}|${d._riga_idx}`;
                  const isDiscard = discardedKeys.has(key);
                  const sameCommessa = d.match_type === "commessa";
                  return (
                    <div
                      key={`${d.rapportino_id}-${d.data}-${d.commessa_codice || ""}-${i}`}
                      className={`px-3 py-2 text-sm ${isDiscard ? "bg-slate-100 opacity-60" : "bg-white"}`}
                      data-testid={`import-dup-row-${i}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-bold">
                            {d.employee_nome || "—"}{" "}
                            <span className="font-normal text-slate-600 text-xs">· {d.data}</span>
                            {sameCommessa && (
                              <span className="ml-2 px-1.5 py-0.5 bg-amber-200 text-amber-900 rounded text-[10px] font-bold tracking-wider">
                                STESSA COMMESSA
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-700 mt-0.5">
                            <span className="text-amber-700">
                              Esistente: <b>#{d.numero}</b> · {d.commessa_codice || "—"} · {Number(d.ore_tot || 0).toFixed(1)}h
                            </span>
                            <span className="mx-1">vs</span>
                            <span className="text-blue-700">
                              Nuovo: {d.input_commessa_codice || "—"} · {Number(d._new_ore_tot || 0).toFixed(1)}h
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button
                            size="sm"
                            variant={!isDiscard ? "default" : "outline"}
                            onClick={() => {
                              if (isDiscard) {
                                const next = new Set(discardedKeys);
                                next.delete(key);
                                setDiscardedKeys(next);
                              }
                            }}
                            className={`h-7 text-xs ${!isDiscard ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}`}
                            data-testid={`import-dup-keep-${i}`}
                          >
                            Tieni
                          </Button>
                          <Button
                            size="sm"
                            variant={isDiscard ? "default" : "outline"}
                            onClick={() => {
                              if (!isDiscard) {
                                const next = new Set(discardedKeys);
                                next.add(key);
                                setDiscardedKeys(next);
                              }
                            }}
                            className={`h-7 text-xs ${isDiscard ? "bg-red-600 hover:bg-red-700 text-white" : "text-red-600"}`}
                            data-testid={`import-dup-discard-${i}`}
                          >
                            Scarta
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Nuove commesse */}
          {(previewData.new_commesse || []).length > 0 && (
            <Card className="p-4 md:p-5 border-blue-200 bg-blue-50/40">
              <div className="flex items-center gap-2 font-heading font-bold text-base mb-3">
                <Briefcase className="h-5 w-5 text-[#0044FF]" />
                Nuove commesse da creare
                <span className="text-xs font-normal text-slate-500">
                  ({(previewData.new_commesse || []).length} non in anagrafica)
                </span>
              </div>
              <div className="space-y-2">
                {(previewData.new_commesse || []).map((nc) => {
                  const choice = commesseChoices[nc.codice] || {};
                  return (
                    <div
                      key={nc.codice}
                      className="flex flex-col md:flex-row md:items-center gap-2 p-2 bg-white rounded border border-slate-200"
                      data-testid={`new-commessa-${nc.codice}`}
                    >
                      <label className="flex items-center gap-2 flex-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!choice.selected}
                          onChange={(e) =>
                            setCommesseChoices({
                              ...commesseChoices,
                              [nc.codice]: { ...choice, selected: e.target.checked },
                            })
                          }
                          className="h-5 w-5 accent-[#0044FF]"
                        />
                        <span className="font-mono font-bold">{nc.codice}</span>
                        <span className="text-xs text-slate-500">
                          · usata in {nc.occurrences} righe
                        </span>
                      </label>
                      <Input
                        placeholder="Descrizione (opzionale)"
                        value={choice.descrizione || ""}
                        onChange={(e) =>
                          setCommesseChoices({
                            ...commesseChoices,
                            [nc.codice]: {
                              ...choice,
                              descrizione: e.target.value,
                            },
                          })
                        }
                        className="h-10 md:max-w-sm"
                        disabled={!choice.selected}
                      />
                      <Input
                        placeholder="Cliente (opzionale)"
                        value={choice.cliente || ""}
                        onChange={(e) =>
                          setCommesseChoices({
                            ...commesseChoices,
                            [nc.codice]: {
                              ...choice,
                              cliente: e.target.value,
                            },
                          })
                        }
                        className="h-10 md:max-w-sm mt-2"
                        disabled={!choice.selected}
                      />
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Dipendenti ambigui */}
          {(previewData.ambiguous_employees || []).length > 0 && (
            <Card className="p-4 md:p-5 border-orange-200 bg-orange-50/40">
              <div className="flex items-center gap-2 font-heading font-bold text-base mb-3">
                <Users className="h-5 w-5 text-orange-700" />
                Nomi ambigui — scegli per ognuno
                <span className="text-xs font-normal text-slate-500">
                  ({(previewData.ambiguous_employees || []).length})
                </span>
              </div>
              <div className="space-y-3">
                {(previewData.ambiguous_employees || []).map((ae) => {
                  const choice = empChoices[ae.raw] || "__skip__";
                  const newForm = newEmpForms[ae.raw] || {};
                  return (
                    <div
                      key={ae.raw}
                      className="p-3 bg-white rounded border border-slate-200 space-y-2"
                      data-testid={`ambiguous-emp-${ae.raw}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <AlertTriangle className="h-4 w-4 text-orange-600" />
                        <span className="text-sm">AI ha letto:</span>
                        <span className="font-mono font-bold bg-slate-50 px-2 py-0.5 rounded text-sm">
                          {ae.raw}
                        </span>
                        <span className="text-xs text-slate-500">
                          · {ae.occurrences} righe
                        </span>
                      </div>
                      <Select
                        value={choice}
                        onValueChange={(v) =>
                          setEmpChoices({ ...empChoices, [ae.raw]: v })
                        }
                      >
                        <SelectTrigger
                          className="h-10"
                          data-testid={`emp-select-${ae.raw}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__skip__">
                            — lascia vuoto / salta —
                          </SelectItem>
                          <SelectItem value="__new__">
                            ➕ Crea nuovo dipendente "{ae.raw}"
                          </SelectItem>
                          {ae.candidates?.length > 0 && (
                            <div className="px-2 py-1 text-xs font-bold text-slate-500 uppercase border-t border-slate-100 mt-1">
                              Candidati simili
                            </div>
                          )}
                          {(ae.candidates || []).map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.cognome} {c.nome}
                              {c.ruolo ? ` · ${c.ruolo}` : ""}
                            </SelectItem>
                          ))}
                          <div className="px-2 py-1 text-xs font-bold text-slate-500 uppercase border-t border-slate-100 mt-1">
                            Tutti i dipendenti
                          </div>
                          {employees
                            .filter(
                              (e) => !ae.candidates?.find((c) => c.id === e.id)
                            )
                            .map((e) => (
                              <SelectItem key={e.id} value={e.id}>
                                {e.cognome} {e.nome}
                                {e.ruolo ? ` · ${e.ruolo}` : ""}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {/* Suggerimento alias - solo se l'utente sceglie un dipendente esistente */}
                      {choice && choice !== "__skip__" && choice !== "__new__" && (
                        <label
                          className="mt-1 flex items-center gap-2 text-xs text-slate-700 cursor-pointer"
                          data-testid={`learn-alias-${ae.raw}`}
                        >
                          <input
                            type="checkbox"
                            checked={learnAliases[ae.raw] ?? true}
                            onChange={(e) =>
                              setLearnAliases({
                                ...learnAliases,
                                [ae.raw]: e.target.checked,
                              })
                            }
                            className="h-4 w-4 accent-[#0044FF]"
                          />
                          <Lightbulb className="h-3.5 w-3.5 text-amber-600" />
                          <span>
                            Ricorda "<b>{ae.raw}</b>" come alias di questo
                            dipendente per i prossimi import
                          </span>
                        </label>
                      )}
                      {choice === "__new__" && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                          <div>
                            <Label className="text-xs font-bold">Cognome</Label>
                            <Input
                              value={newForm.cognome || ""}
                              onChange={(e) =>
                                setNewEmpForms({
                                  ...newEmpForms,
                                  [ae.raw]: { ...newForm, cognome: e.target.value },
                                })
                              }
                              className="h-10"
                            />
                          </div>
                          <div>
                            <Label className="text-xs font-bold">Nome</Label>
                            <Input
                              value={newForm.nome || ""}
                              onChange={(e) =>
                                setNewEmpForms({
                                  ...newEmpForms,
                                  [ae.raw]: { ...newForm, nome: e.target.value },
                                })
                              }
                              className="h-10"
                            />
                          </div>
                          <div>
                            <Label className="text-xs font-bold">Ruolo</Label>
                            <Select
                              value={newForm.ruolo || "Operaio"}
                              onValueChange={(v) =>
                                setNewEmpForms({
                                  ...newEmpForms,
                                  [ae.raw]: { ...newForm, ruolo: v },
                                })
                              }
                            >
                              <SelectTrigger className="h-10">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Operaio">Operaio</SelectItem>
                                <SelectItem value="Capo cantiere">Capo cantiere</SelectItem>
                                <SelectItem value="Autista">Autista</SelectItem>
                                <SelectItem value="Altro">Altro</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Summary */}
          {(previewData.new_commesse || []).length === 0 &&
            (previewData.ambiguous_employees || []).length === 0 && (
              <Card className="p-5 bg-green-50 border-green-200">
                <div className="flex items-center gap-2 text-green-800">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-bold">
                    Tutto chiaro! Nessuna commessa da creare, nessun dipendente
                    ambiguo. Premi "Conferma" per salvare.
                  </span>
                </div>
              </Card>
            )}
        </div>

        <DialogFooter className="mt-3 flex flex-col-reverse md:flex-row gap-2">
          {/* Avviso descrizioni mancanti */}
          {(() => {
            const missingDescr = Object.values(commesseChoices)
              .filter((c) => c?.selected && !(c.descrizione || "").trim());
            if (missingDescr.length === 0) return null;
            return (
              <div
                className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 flex-1 md:mr-auto"
                data-testid="missing-descr-warning"
              >
                ⚠️ Manca la <b>descrizione</b> per{" "}
                {missingDescr.map((c) => c.codice).join(", ")}.
                Aggiungila prima di confermare.
              </div>
            );
          })()}
          <Button
            variant="outline"
            onClick={onCancel}
            className="h-12"
            disabled={saving}
            data-testid="import-cancel-button"
          >
            <X className="h-4 w-4 mr-2" /> Annulla
          </Button>
          <Button
            onClick={finalize}
            disabled={
              saving ||
              Object.values(commesseChoices).some(
                (c) => c?.selected && !(c.descrizione || "").trim()
              )
            }
            className="h-12 bg-[#0044FF] hover:bg-[#0033CC]"
            data-testid="import-confirm-button"
          >
            {saving ? (
              "Salvataggio in corso…"
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Conferma e importa {totRapportini} rapportini
                {newCommesseCount > 0 && ` (+${newCommesseCount} commesse)`}
                {newEmpCount > 0 && ` (+${newEmpCount} dipendenti)`}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
